/**
 * CatalogPersonalizer — Tri du catalogue selon préférences du jour, âge et IA
 *
 * Calcule un score de personnalisation par produit en combinant :
 *  1. Préférences du jour (styles, occasion, budget, catégories)
 *  2. Âge de l'utilisateur (depuis user.birthDate)
 *  3. Analyse IA stockée dans ai_metadata (styleScores, occasions, formality, etc.)
 *
 * Score base = 50, modifié par bonus/pénalités. Plus haut = plus personnel.
 */

// Mapping catégories produits FR → buckets normalisés (cohérent avec TrendingService)
const _CAT_BUCKETS = {
  'robe':'dresses','robes':'dresses','dress':'dresses','habillage':'dresses',
  'haut':'tops','hauts':'tops','t-shirt':'tops','tshirt':'tops','tee-shirt':'tops',
  'chemise':'tops','chemisier':'tops','pull':'tops','sweat':'tops','blouse':'tops','top':'tops','debardeur':'tops',
  'pantalon':'bottoms','pantalons':'bottoms','jean':'bottoms','jeans':'bottoms',
  'short':'bottoms','shorts':'bottoms','jupe':'bottoms','jupes':'bottoms','legging':'bottoms',
  'manteau':'outerwear','veste':'outerwear','blazer':'outerwear','parka':'outerwear','trench':'outerwear','cape':'outerwear',
  'chaussure':'shoes','chaussures':'shoes','basket':'shoes','baskets':'shoes',
  'sneaker':'shoes','sneakers':'shoes','bottes':'shoes','escarpin':'shoes','ballerine':'shoes',
  'sac':'bags','sacs':'bags',
  'accessoire':'accessories','accessoires':'accessories','ceinture':'accessories','echarpe':'accessories','bijou':'accessories','bijoux':'accessories','lunettes':'accessories','chapeau':'accessories','bonnet':'accessories',
};
function _getBucket(product) {
  if (!product?.category) return 'other';
  const cat = String(product.category).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  if (_CAT_BUCKETS[cat]) return _CAT_BUCKETS[cat];
  for (const [key, bucket] of Object.entries(_CAT_BUCKETS)) {
    if (cat.includes(key)) return bucket;
  }
  return 'other';
}

// Mapping pref-category (FR) → bucket
const _PREF_CAT_TO_BUCKET = {
  'Hauts': 'tops', 'Bas': 'bottoms', 'Robes': 'dresses',
  'Vestes': 'outerwear', 'Chaussures': 'shoes', 'Sacs': 'bags',
  'Bijoux': 'accessories', 'Accessoires': 'accessories',
};

/**
 * Calcule l'âge de l'utilisateur à partir de sa date de naissance.
 * Tolère plusieurs formats (YYYY-MM-DD, ISO complet, Date object).
 */
export function computeAge(birthDate) {
  if (!birthDate) return null;
  let d;
  if (typeof birthDate === 'string') {
    const m = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    else d = new Date(birthDate);
  } else d = birthDate instanceof Date ? birthDate : new Date(birthDate);
  if (!d || isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/**
 * Détermine la tranche d'âge stylistique.
 * Influence légère sur le score (ne bloque pas, juste oriente).
 */
function _ageBracket(age) {
  if (age == null) return null;
  if (age < 25) return 'young';        // 18-24 : streetwear/casual/edgy ↑, workwear ↓
  if (age < 35) return 'youngAdult';   // 25-34 : équilibré
  if (age < 50) return 'adult';        // 35-49 : chic/workwear/élégant ↑, streetwear ↓
  return 'senior';                     // 50+   : chic/élégant/minimaliste ↑, edgy ↓
}

// Bonus par tranche d'âge × style (en points sur le styleScore)
const _AGE_STYLE_AFFINITY = {
  young:      { streetwear:+15, edgy:+10, casual:+8,  sport:+8,  bohème:+5,  workwear:-10, élégant:-5 },
  youngAdult: { casual:+5, chic:+5, minimaliste:+5, romantique:+5, streetwear:+3, élégant:+3 },
  adult:      { chic:+12, workwear:+10, élégant:+10, minimaliste:+8, preppy:+5, edgy:-8, streetwear:-8 },
  senior:     { chic:+15, élégant:+15, minimaliste:+10, preppy:+5, edgy:-15, streetwear:-15, sport:-5 },
};

// Mapping budget → fourchette numérique (€)
const _BUDGET_RANGES = {
  'budget':     [0,    50],
  'mid-range':  [50,   150],
  'premium':    [150,  500],
  'luxury':     [500,  Infinity],
  // null = pas de limite → pas de filtrage
};

/**
 * Score un produit selon le profil personnalisé.
 *
 * @param {object} product — produit avec ai_metadata, name, category, price
 * @param {object} prefs   — daily prefs : { styles: [], occasion, budget, categories: [] }
 * @param {number|null} age
 * @returns {number} score (0..200, base = 50)
 */
export function scoreForUser(product, prefs, age = null) {
  if (!product) return 0;
  let score = 50;
  const ai = product.ai_metadata || {};

  // ═══ 1. Préférences STYLES ═══
  // ai_metadata.styleScores : objet { chic: 0-10, casual: 0-10, ... }
  if (Array.isArray(prefs?.styles) && prefs.styles.length > 0 && ai.styleScores) {
    let styleBoost = 0;
    for (const s of prefs.styles) {
      const v = Number(ai.styleScores[s]) || 0;
      // Score IA va de 0-10 → on le multiplie par 4 (max +40 si style=10)
      styleBoost += v * 4;
    }
    // Moyenner sur le nombre de styles cochés (sinon biais énorme si 5 styles cochés)
    score += styleBoost / prefs.styles.length;
  }

  // ═══ 2. Préférence OCCASION ═══
  // ai_metadata.occasions : { bureau: 0-10, soirée: 0-10, ... }
  if (prefs?.occasion && ai.occasions) {
    const v = Number(ai.occasions[prefs.occasion]) || 0;
    // 0-10 → 0-30 points
    score += v * 3;
  }

  // ═══ 3. Préférences CATÉGORIES ═══
  // Si la catégorie du produit est cochée → gros boost (l'user veut voir ÇA)
  if (Array.isArray(prefs?.categories) && prefs.categories.length > 0) {
    const wantedBuckets = new Set(
      prefs.categories.map(c => _PREF_CAT_TO_BUCKET[c]).filter(Boolean)
    );
    const productBucket = _getBucket(product);
    if (wantedBuckets.has(productBucket)) {
      score += 30;
    } else {
      // Pénalité légère pour les catégories non souhaitées
      score -= 8;
    }
  }

  // ═══ 4. Préférence BUDGET ═══
  if (prefs?.budget && _BUDGET_RANGES[prefs.budget]) {
    const [min, max] = _BUDGET_RANGES[prefs.budget];
    const price = Number(product.price) || 0;
    if (price >= min && price <= max) {
      score += 15;
    } else {
      // Hors budget : pénalité proportionnelle à la distance
      const distance = price < min ? (min - price) / Math.max(1, min) : (price - max) / Math.max(1, max);
      score -= Math.min(25, Math.round(distance * 30));
    }
  }

  // ═══ 5. ÂGE ═══
  // Affinité par tranche d'âge × style — appliqué sur ai_metadata.styleScores
  const bracket = _ageBracket(age);
  if (bracket && ai.styleScores) {
    const aff = _AGE_STYLE_AFFINITY[bracket] || {};
    let ageAdjust = 0;
    for (const [style, boost] of Object.entries(aff)) {
      const v = Number(ai.styleScores[style]) || 0;
      // styleScore pondéré : si le produit est très "streetwear" (10) et user jeune (+15) → +15
      // si le produit est "élégant" (8) et user jeune (-5) → -4
      ageAdjust += (v / 10) * boost;
    }
    score += Math.round(ageAdjust);
  }

  // ═══ 6. BONUS TENDANCE ═══
  // Garder une priorité aux produits trending (mais après les prefs)
  if (product.isTrending) {
    score += Math.round((product.trendScore || 50) / 10);  // +5 à +10
  }

  // ═══ 7. BONUS RÉDUCTION ═══
  // Petit boost pour les promos (l'user aime les bonnes affaires)
  if (product.discount && product.discount >= 20) {
    score += 5;
  }

  return Math.max(0, Math.round(score));
}

/**
 * Trie une liste de produits par score de personnalisation décroissant.
 * Si pas de prefs ni d'âge, retourne la liste inchangée (tri stable).
 *
 * @param {Array} products
 * @param {object} prefs   — daily prefs
 * @param {number|null} age
 * @returns {Array} produits triés
 */
export function rankByPersonalization(products, prefs, age = null) {
  if (!Array.isArray(products) || products.length === 0) return products || [];

  const hasPrefs = prefs && (
    (Array.isArray(prefs.styles) && prefs.styles.length > 0) ||
    prefs.occasion ||
    prefs.budget ||
    (Array.isArray(prefs.categories) && prefs.categories.length > 0)
  );
  const hasAge = age != null;
  if (!hasPrefs && !hasAge) return products;

  // Calculer score pour chaque produit (mémoriser dans une Map pour stabilité du sort)
  const scored = products.map(p => ({ p, s: scoreForUser(p, prefs || {}, age) }));
  scored.sort((a, b) => b.s - a.s);

  // Logging diagnostique (top 3 + bottom 3 si beaucoup de produits)
  if (products.length > 0) {
    const top = scored.slice(0, 3).map(x => `${x.p.name?.slice(0, 30) || '?'} (${x.s})`);
    console.log(`[CatalogPersonalizer] ranked ${products.length} products. Top 3: ${top.join(' | ')}`);
  }

  return scored.map(x => x.p);
}
